import React, {useState, useMemo, useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Sidebar} from './components/Sidebar';
import {RuleList} from './components/RuleList';
import {RuleBuilder} from './components/RuleBuilder';
import {Onboarding} from './components/Onboarding';
import {Settings} from './components/Settings';
import {Billing} from './components/Billing';
import {Login} from './components/Login';
import {Register} from './components/Register';
import {IntegrationDetails} from './components/IntegrationDetails';
import {EmailVerificationRequired} from './components/EmailVerificationRequired';
import {
    Rule,
    ViewState,
    Integration,
    Organization,
    Role,
    Member,
    ProviderType,
    User,
    SlackInstallationStatus,
    GitHubInstallationStatus,
    PlanType,
    RulesEnableStatus
} from './types.ts';
import {
    PROVIDER_ICONS,
    AVAILABLE_INTEGRATIONS
} from './constants.ts';
import {CheckCircle2, Menu, GitMerge, Clock, Sparkles} from 'lucide-react';
import {Button} from './components/ui/Button';
import {Tag} from './components/ui/Tag';
import {useAPI} from '@/contexts/APIContext';
import {ApiService, InviteResponse, OrganizationMemberWithStatus, SlackChannel, DowngradePreviewResponse, FreeTierLimitException, FreeTierLimitError, SubscriptionStatus, CheckoutSession, Invoice} from '@/services/apiService';

// Map each view to a URL path so views are deep-linkable and survive refreshes.
// The Settings sub-tabs (profile / organization) live in the URL hash instead.
const VIEW_TO_PATH: Record<ViewState, string> = {
    'dashboard': '/',
    'create-rule': '/rules/new',
    'integrations': '/integrations',
    'settings': '/settings',
    'billing': '/billing',
};

const pathToView = (pathname: string): ViewState => {
    switch (pathname) {
        case '/rules/new':
            return 'create-rule';
        case '/integrations':
            return 'integrations';
        case '/settings':
            return 'settings';
        case '/billing':
            return 'billing';
        default:
            return 'dashboard';
    }
};

const App: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null); // null = checking
    // Initialize authView based on URL path
    const [authView, setAuthView] = useState<'login' | 'register'>(() =>
        location.pathname === '/register' ? 'register' : 'login'
    );

    // currentView is derived from the URL path so deep links / refreshes land on
    // the right page. setCurrentView navigates, keeping all existing call sites unchanged.
    const currentView = pathToView(location.pathname);
    const setCurrentView = (view: ViewState) => {
        navigate(VIEW_TO_PATH[view]);
    };

    // The main content area scrolls independently of the window, so reset its
    // scroll to the top whenever the view (path) changes — otherwise navigating
    // from a scrolled-down page lands on the next page already scrolled down.
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        scrollContainerRef.current?.scrollTo({top: 0});
    }, [location.pathname]);

    // User State
    const [user, setUser] = useState<User | null>(null);
    const [loginError, setLoginError] = useState<string | null>(null);

    // Organization State
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [currentOrganizationId, setCurrentOrganizationId] = useState<string>('');

    // App Data
    const [rules, setRules] = useState<Rule[]>([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [pendingInvites, setPendingInvites] = useState<InviteResponse[]>([]);
    const [orgMembers, setOrgMembers] = useState<OrganizationMemberWithStatus[]>([]);
    const [slackInstallation, setSlackInstallation] = useState<SlackInstallationStatus | null>(null);
    const [slackLoading, setSlackLoading] = useState(false);
    const [githubInstallation, setGithubInstallation] = useState<GitHubInstallationStatus | null>(null);
    const [githubLoading, setGithubLoading] = useState(false);
    const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
    const [rulesStatus, setRulesStatus] = useState<RulesEnableStatus | null>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    // Track in-flight billing fetches so the Billing page can show a loading
    // shimmer instead of flashing "N/A" / "No invoices yet" before data lands.
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [invoicesLoading, setInvoicesLoading] = useState(true);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeModalData, setUpgradeModalData] = useState<FreeTierLimitError | null>(null);

    // UI State
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<Partial<Rule> | undefined>(undefined);
    const [selectedIntegrationProvider, setSelectedIntegrationProvider] = useState<ProviderType | null>(null);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [oauthMessage, setOauthMessage] = useState<{
        type: 'success' | 'error',
        message: string
    } | null>(null);

    // Filter rules for current organization
    const currentOrgRules = useMemo(() => {
        return rules.filter(rule => rule.organizationId === currentOrganizationId);
    }, [rules, currentOrganizationId]);

    // When a trial is set to cancel, surface a countdown in the sidebar so the
    // user can still resume before it ends. Non-null only in that exact state.
    const cancelingTrialDaysLeft = useMemo<number | null>(() => {
        if (!subscriptionStatus) return null;
        if (subscriptionStatus.status !== 'trialing' || !subscriptionStatus.cancel_at_period_end) return null;
        if (!subscriptionStatus.trial_ends_at) return null;
        const msLeft = new Date(subscriptionStatus.trial_ends_at).getTime() - Date.now();
        return Math.max(0, Math.ceil(msLeft / 86_400_000));
    }, [subscriptionStatus]);

    // Handlers
    const handleCreateOrganization = async (name: string) => {
        if (!api) {
            alert('Not connected to API');
            return;
        }
        try {
            const response = await api.createOrganization(name);
            // Refresh user data (includes organizations for sidebar)
            const updatedUser = await api.getUser();
            setUser(updatedUser);
            // Refresh organizations to get full data with members
            const orgs = await api.getOrganizations();
            const mappedOrgs: Organization[] = orgs.map(org => {
                // Look up the user's role from their organization memberships
                const userMembership = updatedUser.organizations.find(uo => uo.id === org.id);
                return {
                    id: org.id,
                    name: org.name,
                    plan: userMembership?.plan || 'free',
                    role: (userMembership?.role as Role) || 'member',
                    members: org.members.map(m => ({
                        id: m.id,
                        name: m.full_name,
                        email: m.email,
                        role: m.role as Role,
                        status: 'active' as const,
                    }))
                };
            });
            setOrganizations(mappedOrgs);
            setCurrentOrganizationId(response.id);
        } catch (err) {
            console.error('Failed to create organization:', err);
            alert('Failed to create organization: ' + (err instanceof Error ? err.message : 'Unknown error'));
        }
    };

    const handleInviteMember = async (email: string, role: Role): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.createInvite(currentOrganizationId, email, role);
        // Refresh org members list to show pending invite
        const members = await api.getOrganizationMembers(currentOrganizationId);
        setOrgMembers(members);
    };

    const handleAcceptInvite = async (inviteId: string): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.acceptInvite(inviteId);
        // Refresh user, organizations, and invites
        const [userData, orgsResponse, invitesResponse] = await Promise.all([
            api.getUser(),
            api.getOrganizations(),
            api.getMyInvites()
        ]);
        setUser(userData);
        const mappedOrgs: Organization[] = orgsResponse.map(org => {
            // Look up the user's role from their organization memberships
            const userMembership = userData.organizations.find(uo => uo.id === org.id);
            return {
                id: org.id,
                name: org.name,
                plan: userMembership?.plan || 'free',
                role: (userMembership?.role as Role) || 'member',
                members: org.members.map(m => ({
                    id: m.id,
                    name: m.full_name,
                    email: m.email,
                    role: m.role as Role,
                    status: 'active' as const,
                }))
            };
        });
        setOrganizations(mappedOrgs);
        setPendingInvites(invitesResponse);
        // Switch to the newly joined organization
        const acceptedInvite = pendingInvites.find(i => i.id === inviteId);
        if (acceptedInvite) {
            setCurrentOrganizationId(acceptedInvite.organization_id);
        }
    };

    const handleRejectInvite = async (inviteId: string): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.rejectInvite(inviteId);
        // Remove from local state
        setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    };

    const handleCancelInvite = async (inviteId: string): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.cancelInvite(currentOrganizationId, inviteId);
        // Refresh org members list
        const members = await api.getOrganizationMembers(currentOrganizationId);
        setOrgMembers(members);
    };

    const handleChangePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.changePassword(currentPassword, newPassword);
    };

    const handleUpgradePlan = async (): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.updateOrganizationPlan(currentOrganizationId, 'pro');
        // Update local organization state
        setOrganizations(prev => prev.map(org =>
            org.id === currentOrganizationId ? {...org, plan: 'pro' as PlanType} : org
        ));
        // Refresh rules status (unlimited rules now allowed)
        fetchRulesStatus();
    };

    const handleGetDowngradePreview = async (): Promise<DowngradePreviewResponse> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        return api.getDowngradePreview(currentOrganizationId);
    };

    // Fetch the Paddle.js config needed to open overlay checkout for Pro.
    const handleStartCheckout = async (): Promise<CheckoutSession> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        return api.createCheckoutSession(currentOrganizationId);
    };

    // Called after Paddle reports checkout completed. Pro is granted server-side
    // by the subscription webhook, so poll billing status until it lands, then
    // sync local state.
    // Returns true once Pro is confirmed server-side (webhook processed), false
    // if it hasn't landed within the polling window.
    const handleCheckoutComplete = async (): Promise<boolean> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        for (let attempt = 0; attempt < 10; attempt++) {
            const status = await api.getBillingStatus(currentOrganizationId);
            if (status.has_subscription) {
                setSubscriptionStatus(status);
                setOrganizations(prev => prev.map(org =>
                    org.id === currentOrganizationId ? {...org, plan: 'pro' as PlanType} : org
                ));
                await fetchInvoices();
                fetchRulesStatus();
                return true;
            }
            await new Promise(resolve => setTimeout(resolve, 2000));
        }
        // Webhook hasn't landed yet; refresh what we can so the UI isn't stale,
        // and report that Pro was not confirmed.
        await handleRefreshSubscription();
        return false;
    };

    // Cancel at the end of the billing period. The org keeps Pro until then.
    const handleCancelSubscription = async (reason?: string): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.cancelSubscription(currentOrganizationId, reason);
        // Refresh subscription status (now flagged cancel_at_period_end).
        fetchSubscriptionStatus();
    };

    // Remove a scheduled cancellation; the subscription keeps running.
    const handleResumeSubscription = async (): Promise<void> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        await api.resumeSubscription(currentOrganizationId);
        // Refresh subscription status (cancel_at_period_end now cleared).
        fetchSubscriptionStatus();
    };

    const handleOpenPortal = async (): Promise<string> => {
        if (!api) {
            throw new Error('Not connected to API');
        }
        const session = await api.createPortalSession(currentOrganizationId);
        return session.url;
    };

    const handleRefreshSubscription = async (): Promise<void> => {
        fetchSubscriptionStatus();
        fetchInvoices();
    };

    const handleNavigateToBilling = () => {
        setCurrentView('billing');
    };

    const handleSkipOnboarding = () => {
        setCurrentView('settings');
    };


    const {api, initAPI, initAPIFromSession} = useAPI();

    // Check for existing session on mount
    useEffect(() => {
        ApiService.checkSession(import.meta.env.VITE_API_BASE_URL || '/api').then(valid => {
            if (valid) {
                // Session is valid, create API service from session
                initAPIFromSession(import.meta.env.VITE_API_BASE_URL || '/api');
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
            }
        });
    }, []);

    const handleLogin = async (email: string, password: string) => {
        setLoginError(null);
        try {
            await initAPI(import.meta.env.VITE_API_BASE_URL || '/api', email, password);
            setIsAuthenticated(true);
            navigate('/');
        } catch (err) {
            setLoginError('Incorrect email or password');
        }
    };

    // Load user data when authenticated and api is available
    useEffect(() => {
        if (isAuthenticated && api) {
            // Fetch both user and orgs, then combine them to get roles
            Promise.all([api.getUser(), api.getOrganizations()]).then(([userData, orgs]) => {
                setUser(userData);
                const mappedOrgs: Organization[] = orgs.map(org => {
                    // Look up the user's role from their organization memberships
                    const userMembership = userData.organizations.find(uo => uo.id === org.id);
                    return {
                        id: org.id,
                        name: org.name,
                        plan: userMembership?.plan || 'free',
                        role: (userMembership?.role as Role) || 'member',
                        members: org.members.map(m => ({
                            id: m.id,
                            name: m.full_name,
                            email: m.email,
                            role: m.role as Role,
                            status: 'active' as const,
                        }))
                    };
                });
                setOrganizations(mappedOrgs);
                if (mappedOrgs.length > 0 && !currentOrganizationId) {
                    setCurrentOrganizationId(mappedOrgs[0].id);
                }
            });
            // Fetch pending invites
            api.getMyInvites().then(invites => {
                setPendingInvites(invites);
            }).catch(err => {
                console.error('Failed to fetch invites:', err);
            });
        }
    }, [isAuthenticated, api]);

    // Handle OAuth and verification callback query params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const slackStatus = params.get('slack');
        const githubStatus = params.get('github');
        const verifiedStatus = params.get('verified');
        const message = params.get('message');

        if (slackStatus === 'success') {
            setOauthMessage({type: 'success', message: 'Slack connected successfully!'});
            setCurrentView('integrations');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (slackStatus === 'error') {
            setOauthMessage({type: 'error', message: message || 'Failed to connect Slack'});
            setCurrentView('integrations');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (githubStatus === 'success') {
            setOauthMessage({type: 'success', message: 'GitHub connected successfully!'});
            setCurrentView('integrations');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (githubStatus === 'error') {
            setOauthMessage({type: 'error', message: message || 'Failed to connect GitHub'});
            setCurrentView('integrations');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (verifiedStatus === 'success') {
            setOauthMessage({type: 'success', message: 'Email verified successfully!'});
            // Refresh user data to get updated is_verified status
            if (api) {
                api.getUser().then(setUser);
            }
            window.history.replaceState({}, '', window.location.pathname);
        } else if (verifiedStatus === 'error') {
            setOauthMessage({type: 'error', message: message || 'Email verification failed'});
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [api]);

    // Clear OAuth message after 5 seconds
    useEffect(() => {
        if (oauthMessage) {
            const timer = setTimeout(() => setOauthMessage(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [oauthMessage]);

    // Fetch org members (including pending invites) when org changes
    useEffect(() => {
        if (isAuthenticated && api && currentOrganizationId) {
            api.getOrganizationMembers(currentOrganizationId)
                .then(members => {
                    setOrgMembers(members);
                })
                .catch(err => {
                    console.error('Failed to fetch org members:', err);
                    setOrgMembers([]);
                });
        }
    }, [isAuthenticated, api, currentOrganizationId]);

    // Fetch Slack installation status when org changes
    useEffect(() => {
        if (isAuthenticated && api && currentOrganizationId) {
            setSlackLoading(true);
            api.getSlackInstallationStatus(currentOrganizationId)
                .then(status => {
                    setSlackInstallation(status);
                })
                .catch(err => {
                    console.error('Failed to fetch Slack status:', err);
                    setSlackInstallation(null);
                })
                .finally(() => {
                    setSlackLoading(false);
                });
        }
    }, [isAuthenticated, api, currentOrganizationId]);

    // Fetch GitHub installation status when org changes
    useEffect(() => {
        if (isAuthenticated && api && currentOrganizationId) {
            setGithubLoading(true);
            api.getGitHubInstallationStatus(currentOrganizationId)
                .then(status => {
                    setGithubInstallation(status);
                })
                .catch(err => {
                    console.error('Failed to fetch GitHub status:', err);
                    setGithubInstallation(null);
                })
                .finally(() => {
                    setGithubLoading(false);
                });
        }
    }, [isAuthenticated, api, currentOrganizationId]);

    // Fetch Slack channels when Slack is connected
    useEffect(() => {
        if (api && currentOrganizationId && slackInstallation?.is_connected) {
            api.getSlackChannels(currentOrganizationId)
                .then(channels => {
                    setSlackChannels(channels);
                })
                .catch(err => {
                    console.error('Failed to fetch Slack channels:', err);
                    setSlackChannels([]);
                });
        } else {
            setSlackChannels([]);
        }
    }, [api, currentOrganizationId, slackInstallation?.is_connected]);

    // Fetch rules and rules status when org changes
    const fetchRulesStatus = () => {
        if (api && currentOrganizationId) {
            api.getRulesStatus(currentOrganizationId)
                .then(status => setRulesStatus({
                    ...status,
                    plan: status.plan as PlanType,
                }))
                .catch(err => {
                    console.error('Failed to fetch rules status:', err);
                    setRulesStatus(null);
                });
        }
    };

    const fetchSubscriptionStatus = () => {
        if (api && currentOrganizationId) {
            setSubscriptionLoading(true);
            api.getBillingStatus(currentOrganizationId)
                .then(status => setSubscriptionStatus(status))
                .catch(err => {
                    console.error('Failed to fetch subscription status:', err);
                    setSubscriptionStatus(null);
                })
                .finally(() => setSubscriptionLoading(false));
        }
    };

    const fetchInvoices = () => {
        if (api && currentOrganizationId) {
            setInvoicesLoading(true);
            api.getInvoices(currentOrganizationId)
                .then(inv => setInvoices(inv))
                .catch(err => {
                    console.error('Failed to fetch invoices:', err);
                    setInvoices([]);
                })
                .finally(() => setInvoicesLoading(false));
        }
    };

    const fetchRules = () => {
        if (api && currentOrganizationId) {
            setRulesLoading(true);
            api.getRules(currentOrganizationId)
                .then(apiRules => {
                    // Convert API response to frontend Rule format
                    const mappedRules: Rule[] = apiRules.map(r => ({
                        id: r.id,
                        organizationId: r.organization_id,
                        name: r.name,
                        description: r.description || '',
                        isActive: r.is_active,
                        source: {
                            provider: r.source.provider as ProviderType,
                            repository: r.source.repository,
                            filters: {
                                teams: r.source.filters.teams,
                                labels: r.source.filters.labels,
                            },
                        },
                        targets: r.targets.map(t => ({
                            id: t.id,
                            provider: t.provider as ProviderType,
                            target: t.target,
                            targetName: t.target_name,
                        })),
                        createdAt: r.created_at,
                    }));
                    setRules(mappedRules);
                })
                .catch(err => {
                    console.error('Failed to fetch rules:', err);
                    setRules([]);
                })
                .finally(() => {
                    setRulesLoading(false);
                });
        }
    };

    useEffect(() => {
        if (isAuthenticated && api && currentOrganizationId) {
            fetchRules();
            fetchRulesStatus();
            fetchSubscriptionStatus();
            fetchInvoices();
        }
    }, [isAuthenticated, api, currentOrganizationId]);

    const handleLogout = async () => {
        if (api) {
            try {
                await api.logout();
            } catch (error) {
                console.error('Logout failed:', error);
            }
        }
        setIsAuthenticated(false);
        window.location.reload();
    };

    // Show loading while checking session
    if (isAuthenticated === null) {
        return (
            <div className="min-h-screen bg-[#f4f1e6] flex items-center justify-center">
                <div className="text-[#4f4f48] font-medium">Loading...</div>
            </div>
        );
    }

    // Show login if not authenticated
    if (!isAuthenticated) {
        if (authView === 'register') {
            return <Register onSwitchToLogin={() => {
                setAuthView('login');
                navigate('/');
            }}/>;
        }
        return <Login onLogin={handleLogin} onSwitchToRegister={() => {
            setAuthView('register');
            navigate('/register');
        }} error={loginError}/>;
    }

    // Show email verification required if not verified
    if (user && !user.is_verified) {
        const handleResendEmail = async () => {
            if (!api) throw new Error('Not connected');
            await api.resendVerificationEmail();
        };

        return (
            <EmailVerificationRequired
                email={user.email}
                onResendEmail={handleResendEmail}
                onLogout={handleLogout}
            />
        );
    }

    // Show loading if user data hasn't loaded yet
    if (!user) {
        return (
            <div className="min-h-screen bg-[#f4f1e6] flex items-center justify-center">
                <div className="text-[#4f4f48] font-medium">Loading...</div>
            </div>
        );
    }

    // If no organizations, show Onboarding or Settings (if user has pending invites or skipped onboarding)
    if (organizations.length === 0) {
        if (pendingInvites.length > 0 || currentView === 'settings') {
            // User has pending invites or skipped onboarding - show Settings without sidebar
            const welcomeMessage = pendingInvites.length > 0
                ? "You have pending invitations. Accept an invite to join an organization, or create your own."
                : "Create a workspace to get started, or wait for an invitation.";
            return (
                <div className="min-h-screen bg-[#f4f1e6] font-sans text-[#0a0a0a]">
                    <div className="max-w-4xl mx-auto p-4 md:p-10">
                        <div className="mb-8 md:mb-10">
                            <div className="flex items-center gap-3 mb-6">
                                <div
                                    className="bg-[#22c55e] p-2 rounded-xl">
                                    <GitMerge size={24} className="text-[#0a0a0a]" strokeWidth={2.5}/>
                                </div>
                                <h1 className="text-2xl font-bold text-[#0a0a0a]">ReviewMe</h1>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a0a0a] mb-2 tracking-tight">
                                Welcome!
                            </h2>
                            <p className="text-base md:text-lg text-[#6f6b60] font-medium leading-snug">
                                {welcomeMessage}
                            </p>
                        </div>
                        <Settings
                            user={user}
                            organizations={organizations}
                            currentOrganizationId={currentOrganizationId}
                            orgMembers={orgMembers}
                            onInviteMember={handleInviteMember}
                            onCreateOrganization={handleCreateOrganization}
                            onSwitchOrganization={setCurrentOrganizationId}
                            pendingInvites={pendingInvites}
                            onAcceptInvite={handleAcceptInvite}
                            onRejectInvite={handleRejectInvite}
                            onCancelInvite={handleCancelInvite}
                            onChangePassword={handleChangePassword}
                        />
                    </div>
                </div>
            );
        }
        return (
            <Onboarding onCreateOrganization={handleCreateOrganization}
                        onSkip={handleSkipOnboarding}/>
        );
    }

    const handleCreateRule = () => {
        setEditingRule(undefined);
        setCurrentView('create-rule');
    };

    const handleEditRule = (rule: Rule) => {
        setEditingRule(rule);
        setCurrentView('create-rule');
    };

    const handleSaveRule = (rule: Rule) => {
        const ruleWithOrg = {...rule, organizationId: rule.organizationId || currentOrganizationId};
        setRules((prev) => {
            const exists = prev.find((r) => r.id === rule.id);
            if (exists) {
                return prev.map((r) => (r.id === rule.id ? ruleWithOrg : r));
            }
            return [ruleWithOrg, ...prev];
        });
        setCurrentView('dashboard');
        setEditingRule(undefined);
        fetchRulesStatus();
    };

    const handleDeleteRule = async (id: string) => {
        if (!api) return;
        if (confirm('Are you sure you want to delete this rule?')) {
            try {
                await api.deleteRule(currentOrganizationId, id);
                setRules((prev) => prev.filter((r) => r.id !== id));
                fetchRulesStatus();
            } catch (err) {
                console.error('Failed to delete rule:', err);
                alert('Failed to delete rule: ' + (err instanceof Error ? err.message : 'Unknown error'));
            }
        }
    };

    const handleToggleRule = async (id: string) => {
        if (!api) return;
        const rule = rules.find(r => r.id === id);
        if (!rule) return;

        try {
            const updatedRule = await api.toggleRule(currentOrganizationId, id, !rule.isActive);
            setRules((prev) =>
                prev.map((r) => r.id === id ? {...r, isActive: updatedRule.is_active} : r)
            );
            fetchRulesStatus();
        } catch (err) {
            if (err instanceof FreeTierLimitException) {
                setUpgradeModalData(err.data);
                setShowUpgradeModal(true);
            } else {
                console.error('Failed to toggle rule:', err);
                alert('Failed to toggle rule: ' + (err instanceof Error ? err.message : 'Unknown error'));
            }
        }
    };

    const handleConnectIntegration = (provider: ProviderType) => {
        if (provider === ProviderType.SLACK) {
            // Redirect to Slack OAuth flow
            window.location.href = `/api/slack/install?organization_id=${currentOrganizationId}`;
            return;
        }
        if (provider === ProviderType.GITHUB) {
            // Redirect to GitHub App installation flow
            window.location.href = `/api/github/install?organization_id=${currentOrganizationId}`;
            return;
        }
        // For other providers, show "Coming Soon" - they shouldn't reach here
        // since the UI will prevent clicking on non-available integrations
        setSelectedIntegrationProvider(null);
    };

    const handleDisconnectIntegration = async (provider: ProviderType) => {
        if (!api) return;

        setIsDisconnecting(true);
        try {
            if (provider === ProviderType.SLACK) {
                await api.disconnectSlack(currentOrganizationId);
                setSlackInstallation(null);
            } else if (provider === ProviderType.GITHUB) {
                await api.disconnectGitHub(currentOrganizationId);
                setGithubInstallation(null);
            }

            // Refresh rules (some may have been cascade-deleted)
            const apiRules = await api.getRules(currentOrganizationId);
            const mappedRules: Rule[] = apiRules.map(r => ({
                id: r.id,
                organizationId: r.organization_id,
                name: r.name,
                description: r.description || '',
                isActive: r.is_active,
                source: {
                    provider: r.source.provider as ProviderType,
                    repository: r.source.repository,
                    filters: {
                        teams: r.source.filters.teams,
                        labels: r.source.filters.labels,
                    },
                },
                targets: r.targets.map(t => ({
                    id: t.id,
                    provider: t.provider as ProviderType,
                    target: t.target,
                    targetName: t.target_name,
                })),
                createdAt: r.created_at,
            }));
            setRules(mappedRules);
            fetchRulesStatus();

            setSelectedIntegrationProvider(null);
        } catch (err) {
            console.error('Failed to disconnect integration:', err);
        } finally {
            setIsDisconnecting(false);
        }
    };

    const getRuleCountForProvider = (provider: ProviderType) => {
        return currentOrgRules.filter(r => r.source.provider === provider).length;
    };

    const getImpactedRulesForProvider = (provider: ProviderType) => {
        return currentOrgRules
            .filter(r => r.source.provider === provider)
            .map(r => ({ id: r.id, name: r.name }));
    };

    return (
        <div className="flex min-h-screen bg-[#f4f1e6] font-sans text-[#0a0a0a]">
            <Sidebar
                currentView={currentView}
                onChangeView={(view) => {
                    if (view === 'create-rule') handleCreateRule();
                    else setCurrentView(view);
                }}
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                user={user}
                organizations={user.organizations}
                currentOrganizationId={currentOrganizationId}
                onSwitchOrganization={setCurrentOrganizationId}
                onLogout={handleLogout}
                onUpgradePlan={handleNavigateToBilling}
                onNavigateToBilling={handleNavigateToBilling}
                cancelingTrialDaysLeft={cancelingTrialDaysLeft}
            />

            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-[#0a0a0a]/40 z-40 md:hidden backdrop-blur-sm"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Upgrade Modal for Free Tier Limit */}
            {showUpgradeModal && upgradeModalData && (
                <div className="fixed inset-0 bg-[#0a0a0a]/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl border-2 border-[#0a0a0a] nb-shadow-lg p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-[#e9d5ff] p-2 rounded-xl border border-[#e4e0d2]">
                                <Sparkles size={20} className="text-[#7e22ce]" />
                            </div>
                            <h3 className="text-lg font-bold text-[#0a0a0a]">Upgrade to Pro</h3>
                        </div>

                        <div className="mb-4 p-4 bg-[#f4f1e6] border border-[#e4e0d2] rounded-xl">
                            <p className="text-[#0a0a0a] font-medium mb-2">
                                Free plan limit: 1 active rule
                            </p>
                            <p className="text-[#4f4f48] text-sm">
                                Your rule "<span className="font-semibold">{upgradeModalData.active_rule_name}</span>" is currently active.
                                Disable it first, or upgrade to Pro for unlimited active rules.
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowUpgradeModal(false);
                                    setUpgradeModalData(null);
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowUpgradeModal(false);
                                    setUpgradeModalData(null);
                                    setCurrentView('billing');
                                }}
                                className="flex-1"
                            >
                                <Sparkles size={16} />
                                Upgrade to Pro
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Integration Details Modal */}
            {selectedIntegrationProvider && (
                <IntegrationDetails
                    definition={AVAILABLE_INTEGRATIONS.find(i => i.provider === selectedIntegrationProvider)!}
                    integration={
                        selectedIntegrationProvider === ProviderType.SLACK && slackInstallation ? {
                            id: 'slack-installation',
                            organizationId: currentOrganizationId,
                            provider: ProviderType.SLACK,
                            name: 'Slack',
                            isConnected: slackInstallation.is_connected,
                            connectedAt: slackInstallation.connected_at,
                            config: {
                                workspace: slackInstallation.team_name,
                                scopes: slackInstallation.scopes
                            }
                        } : selectedIntegrationProvider === ProviderType.GITHUB && githubInstallation ? {
                            id: 'github-installation',
                            organizationId: currentOrganizationId,
                            provider: ProviderType.GITHUB,
                            name: 'GitHub',
                            isConnected: githubInstallation.is_connected,
                            connectedAt: githubInstallation.connected_at,
                            config: {
                                account: githubInstallation.account_login
                            }
                        } : undefined
                    }
                    ruleCount={getRuleCountForProvider(selectedIntegrationProvider)}
                    impactedRules={getImpactedRulesForProvider(selectedIntegrationProvider)}
                    isExternallyDisconnected={
                        (selectedIntegrationProvider === ProviderType.SLACK && slackInstallation?.status === 'error_externally_disconnected') ||
                        (selectedIntegrationProvider === ProviderType.GITHUB && githubInstallation?.status === 'error_externally_disconnected')
                    }
                    isDisconnecting={isDisconnecting}
                    onClose={() => setSelectedIntegrationProvider(null)}
                    onConnect={() => handleConnectIntegration(selectedIntegrationProvider)}
                    onDisconnect={() => handleDisconnectIntegration(selectedIntegrationProvider)}
                    isComingSoon={selectedIntegrationProvider !== ProviderType.SLACK && selectedIntegrationProvider !== ProviderType.GITHUB}
                />
            )}

            <main className="flex-1 md:ml-64 w-full flex flex-col h-screen">
                {/* Mobile Header */}
                <div
                    className="md:hidden bg-white border-b border-[#e4e0d2] p-4 flex items-center justify-between sticky top-0 z-30">
                    <div className="flex items-center gap-3">
                        <div
                            className="bg-[#22c55e] p-1.5 rounded-xl">
                            <GitMerge size={20} className="text-[#0a0a0a]"/>
                        </div>
                        <span className="font-bold text-[#0a0a0a] text-lg">ReviewMe</span>
                    </div>
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 text-[#4f4f48] rounded-xl hover:bg-[#ece8db]"
                    >
                        <Menu size={24}/>
                    </button>
                </div>

                {/* Scrollable Content Area */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 md:p-10">
                    <div className="max-w-5xl mx-auto">

                        {/* Page Header */}
                        <div
                            className="mb-8 md:mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6">
                            <div>
                                <h1 className="text-3xl md:text-4xl font-extrabold text-[#0a0a0a] mb-2 tracking-tight">
                                    {currentView === 'dashboard' && 'Dashboard'}
                                    {currentView === 'create-rule' && 'Rule Editor'}
                                    {currentView === 'integrations' && 'Integrations'}
                                    {currentView === 'settings' && 'Settings'}
                                    {currentView === 'billing' && 'Plans & Billing'}
                                </h1>
                                <p className="text-base md:text-lg text-[#6f6b60] font-medium leading-snug">
                                    {currentView === 'dashboard' && 'Manage your automation workflows.'}
                                    {currentView === 'create-rule' && 'Configure triggers, conditions, and actions.'}
                                    {currentView === 'integrations' && 'Connect your development tools.'}
                                    {currentView === 'settings' && 'Manage your profile and workspace members.'}
                                    {currentView === 'billing' && 'Manage your workspace subscription.'}
                                </p>
                            </div>

                            {currentView === 'dashboard' && (
                                <Button
                                    onClick={handleCreateRule}
                                    disabled={rulesStatus?.can_enable_more === false}
                                    title={rulesStatus?.can_enable_more === false ? 'Free tier: 1 active rule limit. Upgrade to Pro for unlimited rules.' : undefined}
                                    className="w-full md:w-auto px-6 text-lg"
                                >
                                    <span>+</span> New Rule
                                </Button>
                            )}
                        </div>

                        {/* View Content */}
                        <div className="space-y-6 md:space-y-8 pb-10">

                            {currentView === 'dashboard' && (
                                <>
                                    <RuleList
                                        rules={currentOrgRules}
                                        onEdit={handleEditRule}
                                        onDelete={handleDeleteRule}
                                        onToggle={handleToggleRule}
                                        slackChannels={slackChannels}
                                        rulesStatus={rulesStatus}
                                        onUpgrade={handleNavigateToBilling}
                                        loading={rulesLoading && currentOrgRules.length === 0}
                                    />
                                </>
                            )}

                            {currentView === 'create-rule' && (
                                <RuleBuilder
                                    initialData={editingRule}
                                    organizationId={currentOrganizationId}
                                    onSave={handleSaveRule}
                                    onCancel={() => setCurrentView('dashboard')}
                                    onNavigateToIntegrations={() => setCurrentView('integrations')}
                                    slackInstallation={slackInstallation}
                                    githubInstallation={githubInstallation}
                                />
                            )}

                            {currentView === 'integrations' && (
                                <>
                                    {/* OAuth callback message */}
                                    {oauthMessage && (
                                        <div className={`mb-6 p-4 rounded-xl border font-bold ${
                                            oauthMessage.type === 'success'
                                                ? 'bg-[#dcfce7] border-[#bbf7d0] text-[#15803d]'
                                                : 'bg-[#ffe4e6] border-[#fecdd3] text-[#be123c]'
                                        }`}>
                                            {oauthMessage.message}
                                        </div>
                                    )}
                                    <div
                                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {AVAILABLE_INTEGRATIONS.map((available) => {
                                            const isSlack = available.provider === ProviderType.SLACK;
                                            const isGitHub = available.provider === ProviderType.GITHUB;
                                            const isConnected = isSlack
                                                ? (slackInstallation?.is_connected || false)
                                                : isGitHub
                                                    ? (githubInstallation?.is_connected || false)
                                                    : false;
                                            const isComingSoon = !isSlack && !isGitHub;
                                            const isLoading = isSlack ? slackLoading : isGitHub ? githubLoading : false;

                                            return (
                                                <button
                                                    key={available.provider}
                                                    onClick={() => setSelectedIntegrationProvider(available.provider)}
                                                    className={`bg-white p-6 rounded-2xl border border-[#e4e0d2] flex flex-col items-center text-center group cursor-pointer relative ${
                                                        isComingSoon
                                                            ? 'opacity-75 hover:opacity-100'
                                                            : 'card-hover'
                                                    }`}
                                                >
                                                    {isComingSoon && (
                                                        <div className="absolute top-3 right-3">
                                                            <Tag tone="amber">
                                                                <Clock size={12} className="mr-1"/>
                                                                Coming Soon
                                                            </Tag>
                                                        </div>
                                                    )}
                                                    <div
                                                        className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                                                            isConnected
                                                                ? 'bg-[#dcfce7] text-[#15803d]'
                                                                : isComingSoon
                                                                    ? 'bg-[#ece8db] text-[#b8b29e]'
                                                                    : 'bg-[#f4f1e6] text-[#6f6b60] group-hover:bg-[#ece8db]'
                                                        }`}>
                                                        {React.createElement(PROVIDER_ICONS[available.provider], {
                                                            size: 32,
                                                            strokeWidth: 2.5
                                                        })}
                                                    </div>
                                                    <h3 className="text-xl font-bold text-[#0a0a0a] mb-1">{available.name}</h3>
                                                    <p className="text-sm text-[#6f6b60] mb-6 font-bold uppercase tracking-wider">{available.provider}</p>

                                                    <div
                                                        className={`w-full py-2 px-4 rounded-xl font-bold text-sm border flex items-center justify-center gap-2 ${
                                                            isConnected
                                                                ? 'bg-[#dcfce7] border-[#bbf7d0] text-[#15803d]'
                                                                : isComingSoon
                                                                    ? 'bg-[#f4f1e6] border-[#e4e0d2] text-[#9a948a]'
                                                                    : 'bg-[#f4f1e6] border-[#e4e0d2] text-[#6f6b60]'
                                                        }`}
                                                    >
                                                        {isConnected ? (
                                                            <><CheckCircle2 size={16}/> Connected</>
                                                        ) : isComingSoon ? (
                                                            'Not Available'
                                                        ) : isLoading ? (
                                                            'Loading...'
                                                        ) : (
                                                            'Configure'
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {currentView === 'settings' && (
                                <Settings
                                    user={user}
                                    organizations={organizations}
                                    currentOrganizationId={currentOrganizationId}
                                    orgMembers={orgMembers}
                                    onInviteMember={handleInviteMember}
                                    onCreateOrganization={handleCreateOrganization}
                                    onSwitchOrganization={setCurrentOrganizationId}
                                    pendingInvites={pendingInvites}
                                    onAcceptInvite={handleAcceptInvite}
                                    onRejectInvite={handleRejectInvite}
                                    onCancelInvite={handleCancelInvite}
                                    onChangePassword={handleChangePassword}
                                    onUpgradePlan={handleUpgradePlan}
                                    onDowngradePlan={handleCancelSubscription}
                                    onGetDowngradePreview={handleGetDowngradePreview}
                                    onNavigateToBilling={handleNavigateToBilling}
                                />
                            )}

                            {currentView === 'billing' && (
                                <Billing
                                    organization={organizations.find(o => o.id === currentOrganizationId)!}
                                    subscriptionStatus={subscriptionStatus}
                                    invoices={invoices}
                                    subscriptionLoading={subscriptionLoading}
                                    invoicesLoading={invoicesLoading}
                                    onStartCheckout={handleStartCheckout}
                                    onCheckoutComplete={handleCheckoutComplete}
                                    onCancelSubscription={handleCancelSubscription}
                                    onResumeSubscription={handleResumeSubscription}
                                    onOpenPortal={handleOpenPortal}
                                    onRefreshSubscription={handleRefreshSubscription}
                                    onGetDowngradePreview={handleGetDowngradePreview}
                                    userId={user.id}
                                    userEmail={user.email}
                                />
                            )}

                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default App;
